/**
 * Storage key for the client machine identifier (SaaS licensing).
 * Persisted in localStorage so the same device is recognized across sessions.
 */
const MACHINE_ID_STORAGE_KEY = 'saas_machine_id';

/**
 * Generates a new UUID v4 string.
 * Uses crypto.randomUUID() when available (modern browsers/Node), fallback for older envs.
 */
function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: simple v4-like random hex (not strict RFC 4122, but sufficient for machine id)
  const hex = () => Math.floor(Math.random() * 16).toString(16);
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c =>
    c === 'x' ? hex() : ((parseInt(hex(), 16) & 0x3) | 0x8).toString(16)
  );
}

/**
 * Returns the current machine id from localStorage, or creates and stores a new UUID v4.
 * Used by the licensing check (POST /licensing/check) to identify the client device.
 */
export function getOrCreateMachineId(): string {
  try {
    const stored = globalThis.localStorage?.getItem(MACHINE_ID_STORAGE_KEY);
    if (stored && stored.length > 0) {
      return stored;
    }
    const newId = generateUuidV4();
    globalThis.localStorage?.setItem(MACHINE_ID_STORAGE_KEY, newId);
    return newId;
  } catch {
    return generateUuidV4();
  }
}
